import { css } from '@emotion/css';
import React from 'react';
import { TaskType } from 'utils/TaskAPI';
import { isEmpty } from 'lodash';
import { RootState } from 'store/store';
import { useSelector } from 'react-redux';

import Skeleton from './Skeleton';

import icon_calendar from 'assets/images/timeline/icon_calendar.png';
import icon_CGM from 'assets/images/timeline/icon_CGM.png';
import icon_fasting from 'assets/images/timeline/icon_fasting.png';
import icon_feces from 'assets/images/timeline/icon_feces.png';
import icon_foodlog from 'assets/images/timeline/icon_foodlog.png';
import icon_questionnaire from 'assets/images/timeline/icon_questionnaire.png';
import icon_urine from 'assets/images/timeline/icon_urine.png';

import rightArrow from 'assets/icons/right-arrow-black.svg';

export const timelineIcons: Record<string, string> = {
    'icon_calendar': icon_calendar,
    'icon_CGM': icon_CGM,
    'icon_fasting': icon_fasting,
    'icon_feces': icon_feces,
    'icon_foodlog': icon_foodlog,
    'icon_questionnaire': icon_questionnaire,
    'icon_urine': icon_urine,
};

interface TaskCardProps {
    task: TaskType;
    isLast: boolean;
    onOpen: (task: TaskType) => void;
}

const TaskCard = ({ task, isLast, onOpen }: TaskCardProps) => {
    const isLoading = useSelector((state: RootState) => state.global.isLoading);

    const { title, dueOnDate, iconType, isCompleted } = task;

    const handleKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (e) => {
        if (isEmpty(task)) return;
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onOpen(task);
        }
    };

    return (
        <>
            <div
                className={styles.cardRow}
                role="button"
                tabIndex={0}
                onClick={() => !isEmpty(task) && onOpen(task)}
                onKeyDown={handleKeyDown}
                aria-label={`${title}`}
            >
                <Skeleton variant="circular" loading={isLoading}>
                    <div className={styles.iconBlock}>
                        <svg xmlns="http://www.w3.org/2000/svg" width={35} height={35} aria-hidden>
                            <image href={timelineIcons[iconType]} width={35} height={35} />
                        </svg>
                    </div>
                </Skeleton>
                <div className={styles.textContainer}>
                    <div className={styles.innerContent}>
                        <Skeleton loading={isLoading}>
                            <div className={styles.dateContent}>
                                {new Date(dueOnDate).toLocaleString('da-DK', { day: 'numeric', month: 'long' })}
                            </div>
                        </Skeleton>
                        <Skeleton loading={isLoading}>
                            <div className={styles.bottomText}>{isLoading ? 'title' : title}</div>
                        </Skeleton>
                    </div>
                </div>
                <Skeleton variant="circular" loading={isLoading}>
                    <div className={styles.arrowBlock}>
                        {!isCompleted && (
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 100 100"
                                className={styles.arrowSvg}
                                aria-hidden
                            >
                                <image href={rightArrow} width="100" height="100" />
                            </svg>
                        )}
                    </div>
                </Skeleton>
            </div>
            {!isLast && (
                <div className={styles.cardLine}>
                    <div className={styles.cardLineContent}>
                        <div className={styles.lineContainer} style={isLoading ? { borderLeft: 'none' } : {}} />
                    </div>
                </div>
            )}
        </>
    );
};

const styles = {
    cardRow: css({
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        height: '2.6rem',
        paddingLeft: '2rem',
        paddingRight: '2rem',
        boxSizing: 'border-box',
        cursor: 'pointer',
        outline: 'none',
    }),
    iconBlock: css({
        width: '2.1875rem',
        height: '2.1875rem',
        flexShrink: 0,
    }),
    textContainer: css({
        flex: 1,
        display: 'flex',
        justifyContent: 'space-between',
        marginLeft: '0.5rem',
        marginRight: '0.5rem',
        minWidth: 0,
    }),
    innerContent: css({
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        width: '100%',
    }),
    bottomText: css({
        overflow: 'hidden',
        display: '-webkit-box',
        '-webkit-line-clamp': '2',
        '-webkit-box-orient': 'vertical',
        width: '100%',
        minWidth: '50%',
        fontFamily: 'Figtree',
        fontSize: '16px',
        fontStyle: 'normal',
        fontWeight: '400',
        lineHeight: 'normal',
    }),
    dateContent: css({
        width: '100%',
        fontFamily: 'Figtree',
        fontSize: '16px',
        fontStyle: 'normal',
        fontWeight: '600',
        lineHeight: 'normal',
    }),
    arrowBlock: css({
        width: '1.84569rem',
        height: '1.84569rem',
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    }),
    cardLine: css({
        margin: '0px !important',
        display: 'flex',
        alignItems: 'center',
    }),
    cardLineContent: css({
        padding: '0px !important',
        display: 'flex',
        width: '100%',
        justifyContent: 'center',
    }),
    lineContainer: css({
        width: '100%',
        height: '30px',
        marginLeft: '3.05rem',
        borderLeft: '1px solid #333333',
    }),
    arrowSvg: css({
        width: '1.8125rem',
        height: '1.81031rem',
        flexShrink: 0,
    }),
};

export default TaskCard;
